import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@verlo/shared/components/ui/dropdown-menu';

// Saved reports and dashboards as chips along the top (Back office sweep, Oct 2026: D5).
// They used to sit in a list you only reached through Open. The one open is filled; its ⋯
// renames, pins or deletes it; "+ Save" saves what's on screen as a new one. When the one
// open has changed since it was saved, "Update <name>" saves over it.
//
//   <SavedViewChips views={views} activeId={active?.id} dirty={changed} onOpen={open}
//     onSave={saveNew} onUpdate={update} onRename={rename} onDelete={remove} saveLabel="+ Save this view" />
//
// `onRename(view, name)` and `onPin(view)` are optional; without them their menu items go.

export default function SavedViewChips({
  views, activeId, dirty = false, onOpen, onSave, onUpdate, onRename, onPin, onDelete,
  saveLabel = '+ Save this view', noun = 'view', testId = 'saved-views', saveTestId,
}) {
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [confirming, setConfirming] = useState(null); // the view whose delete is being confirmed
  const active = views.find((v) => v.id === activeId);

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`Saved ${noun}s`} data-testid={testId}>
      {views.map((v) => {
        const on = v.id === activeId;
        if (renaming?.id === v.id) {
          return (
            <form
              key={v.id}
              onSubmit={(e) => { e.preventDefault(); const name = renaming.name.trim(); setRenaming(null); if (name && name !== v.name) onRename(v, name); }}
              className="flex h-9 items-center gap-1 rounded-full border border-verlo bg-white pl-3 pr-1"
            >
              <input
                autoFocus
                value={renaming.name}
                maxLength={60}
                onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                onBlur={(e) => e.currentTarget.form.requestSubmit()}
                onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                className="w-40 bg-transparent text-[13.5px] outline-none"
                aria-label={`Rename ${v.name}`}
                data-testid={`${testId}-rename-input`}
              />
            </form>
          );
        }
        return (
          <span
            key={v.id}
            className={`inline-flex h-9 items-center gap-1 rounded-full border pl-3.5 pr-1 text-[13.5px] ${
              on ? 'border-verlo bg-verlo-tint font-semibold text-verlo' : 'border-hairline-strong bg-white text-ink hover:bg-hover-tint'}`}
            data-testid={`${testId}-chip-${v.id}`}
          >
            <button type="button" onClick={() => onOpen(v)} className="max-w-[220px] truncate" aria-pressed={on}>{v.name}</button>
            {v.pinned && <span className="text-[11px] font-normal text-ink-faint" title="Pinned to the Executive dashboard">· pinned</span>}
            <DropdownMenu onOpenChange={(open) => { if (!open) setConfirming(null); }}>
              <DropdownMenuTrigger asChild>
                <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-ink-faint hover:bg-white hover:text-ink" aria-label={`More for ${v.name}`} data-testid={`${testId}-more-${v.id}`}>
                  <MoreHorizontal size={15} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {onRename && <DropdownMenuItem onSelect={() => setRenaming({ id: v.id, name: v.name })}>Rename</DropdownMenuItem>}
                {onPin && <DropdownMenuItem onSelect={() => onPin(v)}>{v.pinned ? 'Unpin from the Executive dashboard' : 'Pin to the Executive dashboard'}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                {confirming === v.id ? (
                  <DropdownMenuItem onSelect={() => onDelete(v)} className="font-semibold text-signal-stop focus:text-signal-stop" data-testid={`${testId}-delete-confirm`}>
                    Delete {v.name}? Yes
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setConfirming(v.id); }} className="text-signal-stop focus:text-signal-stop" data-testid={`${testId}-delete`}>
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        );
      })}
      <button
        type="button"
        onClick={onSave}
        className="h-9 rounded-full border border-dashed border-hairline-strong px-3.5 text-[13.5px] font-semibold text-verlo hover:bg-hover-tint"
        data-testid={saveTestId || `${testId}-save`}
      >
        {saveLabel}
      </button>
      {active && dirty && onUpdate && (
        <span className="text-[12.5px] text-ink-faint">
          Changed since saved ·{' '}
          <button type="button" onClick={() => onUpdate(active)} className="font-semibold text-verlo hover:underline" data-testid={`${testId}-update`}>Update {active.name}</button>
        </span>
      )}
    </div>
  );
}
